import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { cities } from '@barter/shared';

// Города лежат статикой в @barter/shared (с координатами) — поиск и геолокация
// считаются локально, серверный /api/cities не нужен. В контексте поиска
// (главная, /search) через `suggestions` прилетают только города, по которым
// есть активные объявления (+ счётчик к ним); если по запросу среди них
// ничего нет — подсказываем из полного справочника. В форме создания
// объявления suggestions не передаются — показываем весь список.

interface CityItem {
  name: string;
  region: string | null;
  count?: number;
}

export interface CitySuggestion {
  name: string;
  region?: string | null;
  count?: number;
}

interface CitySelectProps {
  value: string;
  onChange: (city: string) => void;
  placeholder?: string;
  small?: boolean;
  /** Города с активными объявлениями (контекст поиска): показываем первыми. */
  suggestions?: CitySuggestion[];
}

function fullList(q: string): CityItem[] {
  return cities
    .filter((c) => !q || c.name.toLowerCase().includes(q) || c.region.toLowerCase().includes(q))
    .map((c) => ({ name: c.name, region: c.region }));
}

function buildItems(query: string, suggestions?: CitySuggestion[]): CityItem[] {
  const q = query.trim().toLowerCase();
  if (!suggestions || suggestions.length === 0) return fullList(q);
  const hits = suggestions
    .filter(
      (c) => !q || c.name.toLowerCase().includes(q) || (c.region ?? '').toLowerCase().includes(q),
    )
    .map((c) => ({ name: c.name, region: c.region ?? null, count: c.count }));
  return hits.length > 0 ? hits : fullList(q);
}

function nearestCity(lat: number, lon: number): CityItem | null {
  let best: (typeof cities)[number] | null = null;
  let bestDist = Infinity;
  for (const c of cities) {
    if (c.lat === undefined || c.lng === undefined) continue;
    const dLat = c.lat - lat;
    const dLon = c.lng - lon;
    const dist = dLat * dLat + dLon * dLon;
    if (dist < bestDist) {
      bestDist = dist;
      best = c;
    }
  }
  return best ? { name: best.name, region: best.region } : null;
}

export function CitySelect({ value, onChange, placeholder, small = false, suggestions }: CitySelectProps) {
  const { t } = useTranslation();
  const _placeholder = placeholder || t('search.city');
  const [query, setQuery] = useState(value);
  const [items, setItems] = useState<CityItem[]>([]);
  const [open, setOpen] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchCities = useCallback(
    (q: string) => {
      setItems(buildItems(q || '', suggestions));
    },
    [suggestions],
  );

  const handleChange = (v: string) => {
    setQuery(v);
    onChange(v);
    setOpen(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fetchCities(v), 150);
  };

  const handleSelect = (city: CityItem) => {
    setQuery(city.name);
    onChange(city.name);
    setOpen(false);
    setItems([]);
  };

  const handleFocus = () => {
    if (!open) {
      setOpen(true);
      fetchCities(query || '');
    }
  };

  const handleGeolocate = () => {
    if (!navigator.geolocation) return;
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const city = nearestCity(pos.coords.latitude, pos.coords.longitude);
        if (city) {
          setQuery(city.name);
          onChange(city.name);
        }
        setGeoLoading(false);
      },
      () => setGeoLoading(false),
      { enableHighAccuracy: false, timeout: 5000 },
    );
  };

  return (
    <div className="city-select" ref={wrapperRef}>
      <div className="city-select__input-row">
        <input
          className={`form-input${small ? ' form-input--small' : ''}`}
          type="text"
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={handleFocus}
          placeholder={_placeholder}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-haspopup="listbox"
        />
        <button
          className="city-select__geo-btn"
          onClick={handleGeolocate}
          disabled={geoLoading}
          type="button"
          title={t('search.detectLocation')}
          aria-label={t('search.detectLocation')}
        >
          {geoLoading ? '⏳' : '📍'}
        </button>
      </div>
      {open && (
        <div className="city-select__dropdown" role="listbox">
          {items.length === 0 && query.length > 0 && (
            <div className="city-select__empty">{t('common.notFound')}</div>
          )}
          {items.map((city) => (
            <button
              key={city.name}
              className="city-select__item"
              onClick={() => handleSelect(city)}
              type="button"
              role="option"
              aria-selected={query === city.name}
            >
              <span className="city-select__name">{city.name}</span>
              {city.region && <span className="city-select__region">{city.region}</span>}
              {city.count !== undefined && <span className="city-select__count">{city.count}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
